import { FC, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { TicketTypeSummary } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal/types';

import styles from './BulkTicketTypeSelect.module.css';

interface BulkTicketTypeSelectProps {
	ticketTypes: TicketTypeSummary[];
	value: number | null;
	onChange: (value: number) => void;
	isInvalid: boolean;
}

/**
 * Styled ticket-type dropdown for table rows.
 *
 * Uses `position: fixed` + `getBoundingClientRect` to escape the table's
 * overflow container, so the panel is never clipped. Mirrors the visual
 * design of PunchupFilterSelect (hover states, selected highlight, border/
 * focus behaviour) using the same --punchup-* tokens.
 */
export const BulkTicketTypeSelect: FC<BulkTicketTypeSelectProps> = ({ ticketTypes, value, onChange, isInvalid }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [panelPos, setPanelPos] = useState({ top: 0, left: 0, minWidth: 0 });
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const selectedType = value !== null ? ticketTypes.find((tt) => tt.iD === value) ?? null : null;

	const updatePos = () => {
		if (!triggerRef.current) return;
		const { bottom, left, width } = triggerRef.current.getBoundingClientRect();
		// Clamp left so the panel (at minimum trigger-width) never overflows the viewport.
		const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - width - 8));
		setPanelPos({ top: bottom + 4, left: clampedLeft, minWidth: width });
	};

	const open = () => {
		updatePos();
		setIsOpen(true);
	};

	const close = () => setIsOpen(false);

	// Reposition when the table scrolls while the panel is open.
	useEffect(() => {
		if (!isOpen) return;
		window.addEventListener('scroll', updatePos, true);
		return () => window.removeEventListener('scroll', updatePos, true);
	}, [isOpen]);

	// Click outside both trigger and panel → close.
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (!panelRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) {
				close();
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isOpen]);

	// Escape → close.
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') close();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [isOpen]);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				className={`${styles.trigger}${isInvalid ? ` ${styles.invalid}` : ''}`}
				onClick={isOpen ? close : open}
			>
				<span className={`${styles.triggerLabel}${selectedType ? '' : ` ${styles.placeholder}`}`}>
					{selectedType ? selectedType.name : 'Select type'}
				</span>
				<ChevronDown size={14} className={`${styles.chevron}${isOpen ? ` ${styles.chevronOpen}` : ''}`} />
			</button>

			{isOpen && (
				<div
					ref={panelRef}
					role="listbox"
					className={styles.panel}
					style={{ top: panelPos.top, left: panelPos.left, minWidth: panelPos.minWidth }}
				>
					{ticketTypes.map((tt) => {
						const isSelected = tt.iD === value;
						// Pool-capped count is computed at the table level (BulkUploadTable.getMaxQty);
						// this select only has the per-type amount/sold, so it shows the standalone
						// remaining. The trigger stays name-only (see CLAUDE.md); the count lives in the option.
						const available = tt.amount - tt.sold;
						return (
							<button
								key={tt.iD}
								type="button"
								role="option"
								aria-selected={isSelected}
								className={`${styles.option}${isSelected ? ` ${styles.optionSelected}` : ''}`}
								onClick={() => {
									onChange(tt.iD);
									close();
								}}
							>
								{tt.name} ({available} available)
							</button>
						);
					})}
				</div>
			)}
		</>
	);
};
